// Universal Amazon storefront routing.
//
// KDP keeps the same ASIN across every marketplace, so a reader in Berlin and a
// reader in Toronto can use the identical product id on their own local store.
// We pick the likely store from the browser's own timezone/language (no IP
// lookup, no third-party service) and always let the reader override it.

export const ASIN = {
  // Book One — separate Kindle and paperback listings.
  kindle: "B0HKF8RYNQ",
  paperback: "B0HKNW67JS",
  // Book Two — one detail page carrying both the Kindle and paperback editions.
  bookTwo: "B0HK8DZCMY",
  author: "B0HKHF32MN"
};

const DEFAULT_STORE = "US";

// Amazon Associates tracking ids, per marketplace. A tag only works on the
// store it was issued for, so stores without an id stay untagged.
// Add more as you are approved for other Associates programmes, e.g.
//   GB: "tylegn-21", DE: "tylegn-21"
export const STORE_TAGS = {
  US: "tylegn-20"
};

/** Append the Associates tag for a store, when one exists. */
const withTag = (url, storeCode) => {
  const tag = STORE_TAGS[storeCode];
  if (!tag) return url;
  return `${url}${url.includes("?") ? "&" : "?"}tag=${encodeURIComponent(tag)}`;
};

// Every KDP marketplace. `print` marks stores that print/stock paperbacks;
// the rest still sell the paperback as an import, which is what we want.
export const STORES = [
  { code: "US", tld: "com", label: "United States", flag: "🇺🇸", print: true },
  { code: "GB", tld: "co.uk", label: "United Kingdom", flag: "🇬🇧", print: true },
  { code: "DE", tld: "de", label: "Germany", flag: "🇩🇪", print: true },
  { code: "FR", tld: "fr", label: "France", flag: "🇫🇷", print: true },
  { code: "ES", tld: "es", label: "Spain", flag: "🇪🇸", print: true },
  { code: "IT", tld: "it", label: "Italy", flag: "🇮🇹", print: true },
  { code: "NL", tld: "nl", label: "Netherlands", flag: "🇳🇱", print: true },
  { code: "PL", tld: "pl", label: "Poland", flag: "🇵🇱", print: true },
  { code: "SE", tld: "se", label: "Sweden", flag: "🇸🇪", print: true },
  { code: "JP", tld: "co.jp", label: "Japan", flag: "🇯🇵", print: true },
  { code: "CA", tld: "ca", label: "Canada", flag: "🇨🇦", print: true },
  { code: "AU", tld: "com.au", label: "Australia", flag: "🇦🇺", print: true },
  { code: "IN", tld: "in", label: "India", flag: "🇮🇳", print: false },
  { code: "BR", tld: "com.br", label: "Brazil", flag: "🇧🇷", print: false },
  { code: "MX", tld: "com.mx", label: "Mexico", flag: "🇲🇽", print: false }
];

const BY_CODE = STORES.reduce((acc, s) => {
  acc[s.code] = s;
  return acc;
}, {});

export const getStore = (code) => BY_CODE[code] || BY_CODE[DEFAULT_STORE];

// Countries that share a marketplace with a listed store.
const COUNTRY_TO_STORE = {
  // United Kingdom & Ireland
  GB: "GB", IE: "GB",
  // Germany / Austria / Switzerland / Liechtenstein
  DE: "DE", AT: "DE", CH: "DE", LI: "DE",
  // France / Belgium / Luxembourg / Monaco
  FR: "FR", BE: "FR", LU: "FR", MC: "FR",
  // Spain & Andorra
  ES: "ES", AD: "ES",
  IT: "IT", SM: "IT", VA: "IT",
  NL: "NL",
  PL: "PL",
  SE: "SE", NO: "SE", DK: "SE", FI: "SE",
  JP: "JP",
  CA: "CA",
  AU: "AU", NZ: "AU",
  IN: "IN",
  BR: "BR",
  MX: "MX",
  US: "US"
};

// Timezone → country. Covers the common zones for our marketplaces so we can
// place a reader even when their browser language is set to plain "en".
const ZONE_TO_COUNTRY = {
  "Europe/London": "GB", "Europe/Dublin": "IE",
  "Europe/Berlin": "DE", "Europe/Vienna": "AT", "Europe/Zurich": "CH",
  "Europe/Busingen": "DE", "Europe/Vaduz": "LI",
  "Europe/Paris": "FR", "Europe/Brussels": "BE", "Europe/Luxembourg": "LU",
  "Europe/Monaco": "MC",
  "Europe/Madrid": "ES", "Atlantic/Canary": "ES", "Africa/Ceuta": "ES",
  "Europe/Andorra": "AD",
  "Europe/Rome": "IT", "Europe/San_Marino": "SM", "Europe/Vatican": "VA",
  "Europe/Amsterdam": "NL",
  "Europe/Warsaw": "PL",
  "Europe/Stockholm": "SE", "Europe/Oslo": "NO", "Europe/Copenhagen": "DK",
  "Europe/Helsinki": "FI",
  "Asia/Tokyo": "JP",
  "Asia/Kolkata": "IN", "Asia/Calcutta": "IN",
  "America/Sao_Paulo": "BR", "America/Bahia": "BR", "America/Fortaleza": "BR",
  "America/Recife": "BR", "America/Manaus": "BR", "America/Belem": "BR",
  "America/Mexico_City": "MX", "America/Monterrey": "MX",
  "America/Tijuana": "MX", "America/Merida": "MX", "America/Cancun": "MX",
  "America/Chihuahua": "MX", "America/Mazatlan": "MX",
  "America/Toronto": "CA", "America/Vancouver": "CA", "America/Edmonton": "CA",
  "America/Winnipeg": "CA", "America/Halifax": "CA", "America/Montreal": "CA",
  "America/St_Johns": "CA", "America/Regina": "CA", "America/Moncton": "CA",
  "Australia/Sydney": "AU", "Australia/Melbourne": "AU",
  "Australia/Brisbane": "AU", "Australia/Perth": "AU",
  "Australia/Adelaide": "AU", "Australia/Hobart": "AU",
  "Australia/Darwin": "AU", "Pacific/Auckland": "NZ"
};

const countryFromTimezone = () => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return ZONE_TO_COUNTRY[zone] || null;
  } catch {
    return null;
  }
};

const countryFromLanguage = () => {
  const tags =
    typeof navigator === "undefined"
      ? []
      : navigator.languages?.length
      ? navigator.languages
      : [navigator.language].filter(Boolean);

  for (const tag of tags) {
    // "en-GB" / "de-AT" → region subtag
    const region = String(tag).split("-")[1];
    if (region && COUNTRY_TO_STORE[region.toUpperCase()]) {
      return region.toUpperCase();
    }
  }
  return null;
};

/** Best guess at the reader's marketplace, never throws. */
export const detectStoreCode = () => {
  const country = countryFromTimezone() || countryFromLanguage();
  return (country && COUNTRY_TO_STORE[country]) || DEFAULT_STORE;
};

/** Amazon product URL for a format on a given store. */
export const productUrl = (storeCode, format) => {
  const store = getStore(storeCode);
  const asin = ASIN[format] || ASIN.kindle;
  return withTag(`https://www.amazon.${store.tld}/dp/${asin}`, store.code);
};

/** Amazon product URL for an explicit ASIN on a given store. */
export const asinUrl = (storeCode, asin) => {
  const store = getStore(storeCode);
  return withTag(
    `https://www.amazon.${store.tld}/dp/${asin || ASIN.kindle}`,
    store.code
  );
};

/** Author page on a given store (same author id everywhere). */
export const authorUrl = (storeCode) => {
  const store = getStore(storeCode);
  return withTag(
    `https://www.amazon.${store.tld}/-/e/${ASIN.author}`,
    store.code
  );
};
